// ** React imports
import React, { useState, useEffect } from 'react'

// ** MUI Imports
import {
  Grid,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material'
import IconButton from '@mui/material/IconButton'

// ** Components Imports
import ConversionRateCard from '../components/ConversionRateCard'

// ** API Imports
import { getConversionRate, getAllConversionRates } from 'configs/axios/api_helper'

// ** Utils Imports
import moment from 'moment'

// ** Icons Imports
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

const ConversionRates = () => {
  const [conversionRates, setConversionRates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [history, setHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(true)
  const [historyError, setHistoryError] = useState(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    fetchConversionRates()
  }, [])

  useEffect(() => {
    fetchAllConversionRates()
  }, [page])

  const fetchConversionRates = async () => {
    try {
      setLoading(true)
      setError(null)

      // Get the start of current month using moment
      const startOfMonth = moment().startOf('month').format('YYYY-MM-DD')
      const response = await getConversionRate(startOfMonth)

      if (response && response.data && Array.isArray(response.data)) {
        console.log('Conversion Rates API response:', response.data)
        // Transform API response to match our component structure
        const currencyIcons = {
          USD: <AttachMoneyIcon sx={{ color: 'common.black' }} />,
          EUR: <CurrencyExchangeIcon sx={{ color: 'common.black' }} />,
          GBP: <TrendingUpIcon sx={{ color: 'common.black' }} />
        }

        const rates = response.data.map((item, index) => ({
          id: `cad-${item.currency.toLowerCase()}`,
          from: item.currency,
          to: 'CAD',
          rate: parseFloat(item.rate) || 0,
          icon: currencyIcons[item.currency] || <AttachMoneyIcon sx={{ color: 'common.black' }} />
        }))

        // Optional USD -> EUR card : only shown when the API includes usd_convertion_rate on the EUR entry
        const eurItem = response.data.find(item => item.currency === 'EUR')
        if (eurItem?.usd_convertion_rate != null) {
          rates.push({
            id: 'usd-eur',
            from: 'USD',
            to: 'EUR',
            rate: parseFloat(eurItem.usd_convertion_rate) || 0,
            icon: <CurrencyExchangeIcon sx={{ color: 'common.black' }} />
          })
        }

        // Optional USD -> GBP card : only shown when the API includes usd_convertion_rate on the GBP entry
        const gbpItem = response.data.find(item => item.currency === 'GBP')
        if (gbpItem?.usd_convertion_rate != null) {
          rates.push({
            id: 'usd-gbp',
            from: 'USD',
            to: 'GBP',
            rate: parseFloat(gbpItem.usd_convertion_rate) || 0,
            icon: <TrendingUpIcon sx={{ color: 'common.black' }} />
          })
        }

        setConversionRates(rates)
      } else {
        throw new Error('No data received from API')
      }
    } catch (err) {
      console.error('Error fetching conversion rates:', err)
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load conversion rates'
      setError(`Unable to load conversion rates: ${errorMessage}`)
      setConversionRates([])
    } finally {
      setLoading(false)
    }
  }

  const fetchAllConversionRates = async () => {
    try {
      setHistoryLoading(true)
      setHistoryError(null)

      const response = await getAllConversionRates(page)

      if (!response || !response.data) {
        throw new Error('No data received from API')
      }

      const raw = response.data.data

      if (!Array.isArray(raw)) {
        throw new Error('Unexpected payload shape from getAllConversionRates')
      }

      const groups = raw.reduce((acc, item) => {
        const date = item.day || item.date
        if (!date) return acc
        if (!acc[date]) acc[date] = []
        acc[date].push(item)
        return acc
      }, {})

      const normalized = Object.keys(groups).map(date => {
        const monthLabel = moment(date).format('MMM YYYY')
        const map = {}
        groups[date].forEach(item => {
          const currency = item.currency || item.code || item.ccy
          const rate = parseFloat(item.convertion_rate ?? item.rate ?? item.value ?? item.fx) || null
          if (currency) map[currency] = rate
        })
        return {
          date,
          month: monthLabel,
          USD: map['USD'] ?? map['usd'] ?? null,
          EUR: map['EUR'] ?? map['eur'] ?? null,
          GBP: map['GBP'] ?? map['gbp'] ?? null
        }
      })

      // sort by date desc (most recent first)
      normalized.sort((a, b) => {
        const da = moment(a.date)
        const db = moment(b.date)
        if (!da.isValid() || !db.isValid()) return 0
        return db.valueOf() - da.valueOf()
      })

      setHistory(normalized)
      setTotalPages(response.data.totalPages)
    } catch (err) {
      console.error('Error fetching conversion history:', err)
      setHistoryError('Unable to load historical conversion rates')
      setHistory([])
    } finally {
      setHistoryLoading(false)
    }
  }

  const goToPrevPage = () => {
    if (page === 1) return
    const newPage = page - 1
    setPage(newPage)
  }

  const goToNextPage = () => {
    if (page >= totalPages) return
    const newPage = page + 1
    setPage(newPage)
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert
          severity='error'
          sx={{ mb: 3 }}
          action={
            <Button color='inherit' size='small' onClick={fetchConversionRates}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {conversionRates.length === 0 && !loading && !error && (
        <Alert severity='info' sx={{ mb: 3 }}>
          No conversion rates available for the current period.
        </Alert>
      )}

      {conversionRates.length > 0 && (
        <Grid container spacing={4}>
          {conversionRates.map(rate => (
            <Grid item xs={12} md={4} key={rate.id}>
              <ConversionRateCard icon={rate.icon} from={rate.from} to={rate.to} rate={rate.rate} />
            </Grid>
          ))}
        </Grid>
      )}
      {/* Historical table */}
      <Box sx={{ mt: 4 }}>
        <Typography variant='h6' sx={{ mb: 2 }}>
          Conversion Rates History
        </Typography>

        {historyLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : historyError ? (
          <Alert
            severity='error'
            sx={{ mb: 2 }}
            action={
              <Button color='inherit' size='small' onClick={() => fetchAllConversionRates()}>
                Retry
              </Button>
            }
          >
            {historyError}
          </Alert>
        ) : history.length === 0 ? (
          <Alert severity='info'>No historical data available.</Alert>
        ) : (
          <TableContainer component={Paper} sx={{ boxShadow: 0, borderRadius: 1, overflow: 'hidden' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align='right'>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <AttachMoneyIcon fontSize='small' sx={{ mr: 1 }} />
                      USD → CAD
                    </Box>
                  </TableCell>
                  <TableCell align='right'>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <TrendingUpIcon fontSize='small' sx={{ mr: 1 }} />
                      GBP → CAD
                    </Box>
                  </TableCell>
                  <TableCell align='right'>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <CurrencyExchangeIcon fontSize='small' sx={{ mr: 1 }} />
                      EUR → CAD
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map(row => (
                  <TableRow key={row.month} hover>
                    <TableCell component='th' scope='row'>
                      <Box>
                        <Typography>{row.month}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align='right'>{row.USD !== null ? row.USD.toFixed(5) : '—'}</TableCell>
                    <TableCell align='right'>{row.GBP !== null ? row.GBP.toFixed(5) : '—'}</TableCell>
                    <TableCell align='right'>{row.EUR !== null ? row.EUR.toFixed(5) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {/* Pagination controls: 12 months per page (one year) */}
        {!historyLoading && !historyError && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 1 }}>
            <IconButton
              size='small'
              onClick={goToPrevPage}
              disabled={page === 1 || historyLoading}
              aria-label='previous page'
            >
              <ChevronLeftIcon />
            </IconButton>

            <Typography variant='body2' sx={{ mx: 1 }}>
              Page {page}
            </Typography>

            <IconButton
              size='small'
              onClick={goToNextPage}
              disabled={page === totalPages || historyLoading}
              aria-label='next page'
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default ConversionRates
